import { Component, ChangeDetectionStrategy, ChangeDetectorRef,
  EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { CfcInvestmentAdjEditService } from './cfc-investment-adj-edit.service';

@Component({
    selector: 'gtw-cfc-investment-adj-edit',
    templateUrl: './cfc-investment-adj-edit.component.html',
    styleUrls: ['./cfc-investment-adj-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CfcInvestmentAdjEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;

  GENERAL_CONFIG: any = {};
  private globalParams: any = {};
  filterParams: any = {};
  USER_SETTINGS: any = {};

  minimise = false;
  loadingData = true;
  crudLoading = false;
  isSaveClicked = false;
  isSaveAndClose = false;
  savedOnce = false;

  selectedGridRow: any[] = [];
  cfc_combination_key = 0;
  adjObj: any = {};
  adjustmentDtls: any[] = [];
  selectedAdjType = '';
  adjustmentTypeDropdown: any[] = [];
  totalAdjAmt = 0;
  is_locked: any = null;
  scenario = '';
  jcd_key = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private cfcInvestmentAdjEditService: CfcInvestmentAdjEditService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalParams = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    const gridData = gridPayloadFromActiveScreen(activeScreen);
    if (gridData && gridData.rowData) {
      this.selectedGridRow = [_.clone(gridData.rowData)];
      this.cfc_combination_key = this.selectedGridRow[0]?.CFC_COMBINATION_KEY || 0;
    }
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    if (!this.cfc_combination_key) return;
    this.isInitialized = true;

    this.scenario = this.filterParams?.scenario || this.globalParams?.scenario || '';
    this.jcd_key = this.filterParams?.jcd_key || this.globalParams?.jcd_key || '';

    this.checkLockStatus();
    this.getAdjustmentTypes();
    this.getAdjustmentDtls();
  }

  private checkLockStatus(): void {
    this.cfcInvestmentAdjEditService.checkLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcd_key,
      this.globalParams,
      this.filterParams,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.is_locked = data.jsonObject;
        if (this.is_locked?.[0]?.LOCK_STATUS == 'Y') {
          this.cancel();
          this.gtwSnackbarService.openSnackBar(
            `Tax Return has already been filed for tax year ${this.filterParams.tax_year}.`,
            'gtw-snackbar--red',
          );
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      },
    );
  }

  private getAdjustmentTypes(): void {
    this.cfcInvestmentAdjEditService.getAdjustmentTypes(
      this.GENERAL_CONFIG.base_url,
      this.filterParams?.tax_year || this.globalParams?.tax_year || '',
      this.globalParams,
      this.filterParams,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response?.jsonObject) {
          this.adjustmentTypeDropdown = response.jsonObject;
          this.selectedAdjType = '';
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading adjustment types:', error);
        this.gtwSnackbarService.openSnackBar('Error loading adjustment types', 'gtw-snackbar--red');
      },
    );
  }

  private getAdjustmentDtls(): void {
    this.cfcInvestmentAdjEditService.getAdjustmentDtls(
      this.GENERAL_CONFIG.base_url,
      this.cfc_combination_key,
      this.globalParams,
      this.filterParams,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response?.jsonObject) {
          this.adjustmentDtls = response.jsonObject;
          this.calculateTotalAdjAmount();
        }
        this.loadingData = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading adjustment details:', error);
        this.loadingData = false;
        this.gtwSnackbarService.openSnackBar('Error loading adjustment details', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    );
  }

  editRow(editObj: any): void {
    const tempObj: any = {};
    tempObj.operation_type = 'U';
    tempObj.adj_key = editObj.ADJ_KEY;
    tempObj.adj_type_key = editObj.ADJ_TYPE_KEY;
    tempObj.adj_desc = editObj.ADJ_DESC;

    const dateSplitted = (editObj.ADJ_DATE || '').split('-');
    if (dateSplitted.length === 3) {
      if (dateSplitted[0].length === 4) {
        tempObj.adj_date = editObj.ADJ_DATE;
      } else {
        tempObj.adj_date = `${dateSplitted[2]}-${dateSplitted[1].padStart(2, '0')}-${dateSplitted[0].padStart(2, '0')}`;
      }
    } else {
      const date = new Date(editObj.ADJ_DATE);
      tempObj.adj_date = !isNaN(date.getTime()) ? this.formatDateForInput(date) : '';
    }

    tempObj.amount = editObj.AMOUNT;
    this.adjObj = tempObj;
    this.selectedAdjType = editObj.ADJ_TYPE_KEY || '';
    this.cdr.detectChanges();
  }

  saveAndClose(): void {
    this.isSaveAndClose = true;
    this.save();
  }

  save(): void {
    this.isSaveClicked = true;
    this.roundAmountToTwoDecimals();

    if (!this.selectedAdjType || this.adjObj.adj_date == null || this.adjObj.amount == null) {
      this.gtwSnackbarService.openSnackBar(
        'Please select/enter all the required fields.',
        'gtw-snackbar--red',
      );
      this.isSaveClicked = false;
      return;
    }

    if (this.is_locked?.[0]?.LOCK_STATUS == 'Y') {
      this.cancel();
      this.gtwSnackbarService.openSnackBar(
        'You cannot perform this action as Tax return is already filed.',
        'gtw-snackbar--red',
      );
      this.isSaveClicked = false;
      return;
    }

    this.prepAndSendDetails();
  }

  private prepAndSendDetails(): void {
    const returnClobSettingsObj: any = {
      sso_id: this.USER_SETTINGS?.user?.sso_id || '',
      object_id: this.cfc_combination_key,
    };

    this.adjObj.cfc_combination_key = this.cfc_combination_key;

    if (this.adjObj.adj_date && typeof this.adjObj.adj_date === 'string') {
      const dateParts = this.adjObj.adj_date.split('-');
      if (dateParts.length === 3 && dateParts[0].length === 4) {
        this.adjObj.adj_date = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
      }
    }

    this.adjObj.adj_type_key = this.selectedAdjType;

    this.sendDetails(
      returnClobSettingsObj,
      this.adjObj,
      'Adjustment Amount(s) have been saved/updated',
    );
  }

  private sendDetails(returnClobSettingsObj: any, defaultsChangeDetails: any, message: string): void {
    this.cfcInvestmentAdjEditService.saveData(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      defaultsChangeDetails,
      this.globalParams,
      this.filterParams,
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          this.isSaveClicked = false;
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');

          if (this.isSaveAndClose) {
            this.requestGridUpdate.emit('refresh');
            this.cancel();
          } else {
            this.reset();
            this.getAdjustmentDtls();
            this.savedOnce = true;
          }

          this.isSaveClicked = false;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while saving. Please try again.',
          'gtw-snackbar--red',
        );
        this.cdr.detectChanges();
      },
    );
  }

  private calculateTotalAdjAmount(): void {
    this.totalAdjAmt = 0;
    for (const item of this.adjustmentDtls) {
      this.totalAdjAmt += (item.AMOUNT || 0);
    }
    this.totalAdjAmt = parseFloat(this.totalAdjAmt.toFixed(2));
  }

  reset(): void {
    this.adjObj = {};
    this.selectedAdjType = '';
  }

  cancel(): void {
    if (this.savedOnce) {
      this.requestGridUpdate.emit('refresh');
    } else {
      this.requestGridUpdate.emit('cancel');
    }
  }

  private formatDateForInput(date: Date): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getSortedAdjustmentDtls(): any[] {
    if (!this.adjustmentDtls) return [];
    return [...this.adjustmentDtls].sort((a, b) => (a.ADJ_KEY || 0) - (b.ADJ_KEY || 0));
  }

  trackByAdjKey(index: number, item: any): any {
    return item.ADJ_KEY ?? index;
  }

  roundAmountToTwoDecimals(): void {
    if (this.adjObj.amount != null && this.adjObj.amount !== '') {
      const numValue = parseFloat(this.adjObj.amount.toString());
      if (!isNaN(numValue)) {
        this.adjObj.amount = parseFloat(numValue.toFixed(2));
      }
    }
  }
}
